/**
 * THE SINGLE DECISION: MAY THIS PAGE VIEW BE MEASURED?
 *
 * One pure function, taking everything it needs and reaching for nothing. It
 * is the only place the six gates are combined, so "what has to be true"
 * exists once and can be read in one screen rather than reconstructed from
 * four call sites.
 *
 * The result carries WHY when the answer is no. Not for a payload — for the
 * tests and for the operator checklist, which both have to be able to
 * distinguish "the flag is off" from "this is a credential route", because
 * those two failures need completely different responses from a human.
 */
import { evaluateGates, type GateFailure } from "./gates";
import { isGranted, type ConsentState } from "./consent";
import { categoryFor, isCredentialRoute, type Category } from "./routes";

export type Refusal =
  | GateFailure
  | "credential-route"
  | "ineligible-route"
  | "consent-unknown"
  | "consent-denied"
  | "consent-unreadable";

export type Decision =
  | { readonly collect: true; readonly measurementId: string; readonly category: Category }
  | { readonly collect: false; readonly refusals: readonly Refusal[] };

export interface DecisionInput {
  readonly hostname: string;
  readonly pathname: string;
  readonly consent: ConsentState;
}

/**
 * EVERY refusal is collected, not the first one.
 *
 * Short-circuiting would be cheaper and would make the tests worse: a build
 * that is wrong in three ways would report one, someone would fix it, and the
 * next run would report the next. The operator checklist needs the whole list
 * at once.
 */
export function decide({ hostname, pathname, consent }: DecisionInput): Decision {
  const refusals: Refusal[] = [];

  // FIRST, and independent of everything else. A credential route is refused
  // on a build where every other gate passes and the visitor said yes — which
  // is exactly the case the ordering has to survive.
  if (isCredentialRoute(pathname)) refusals.push("credential-route");

  const gates = evaluateGates(hostname);
  if (!gates.eligible) refusals.push(...gates.failures);

  if (!isGranted(consent)) {
    refusals.push(
      consent === "denied"
        ? "consent-denied"
        : consent === "unreadable"
          ? "consent-unreadable"
          : "consent-unknown"
    );
  }

  const category = categoryFor(pathname);
  if (category === null && !isCredentialRoute(pathname)) refusals.push("ineligible-route");

  if (refusals.length > 0 || category === null || !gates.eligible) {
    // The second half of that condition is not redundant: it is what makes
    // the type narrow, and a `!` here instead would be an assertion that the
    // gates agree with the refusal list — which is the assumption this
    // function exists to remove.
    return { collect: false, refusals: refusals.length > 0 ? refusals : ["ineligible-route"] };
  }

  return { collect: true, measurementId: gates.measurementId, category };
}
